import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

import { api } from '@/lib/api'

interface VerifyResponse {
  success: boolean
  data?: {
    email?: string
    token?: string
  }
  error?: string
}

type Status = 'verifying' | 'success' | 'error'

export function VerifyEmailPage() {
  const [status, setStatus] = useState<Status>('verifying')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const logo = useMemo(() => '/logo.png', [])

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setStatus('error')
      setMessage('Invalid verification link. Please check your email or sign up again.')
      return
    }

    verifyEmailToken(token)
  }, [searchParams])

  const verifyEmailToken = async (token: string) => {
    try {
      const result = await api.post<VerifyResponse>('/auth/verify-email', { token })

      if (result.success) {
        setStatus('success')
        setMessage('Your email has been successfully verified!')
        if (result.data?.email) {
          setEmail(result.data.email)
        }

        if (result.data?.token) {
          await queryClient.invalidateQueries({ queryKey: ['auth'] })
          setTimeout(() => {
            navigate('/onboarding')
          }, 2000)
        } else {
          setTimeout(() => {
            navigate('/login?verified=true')
          }, 2000)
        }
      } else {
        setStatus('error')
        setMessage(result.error || 'Email verification failed. The link may be expired or invalid.')
      }
    } catch (err) {
      console.error('Email verification failed', err)
      setStatus('error')
      setMessage('Something went wrong during verification. Please try again.')
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'verifying':
        return <Loader2 className="w-8 h-8 text-primary animate-spin" />
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-600" />
      case 'error':
        return <AlertCircle className="w-8 h-8 text-destructive" />
      default:
        return null
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'verifying':
        return 'bg-surface-variant'
      case 'success':
        return 'bg-green-100'
      case 'error':
        return 'bg-destructive/8'
      default:
        return 'bg-surface-variant'
    }
  }

  const getTitle = () => {
    switch (status) {
      case 'verifying':
        return 'Verifying Your Email...'
      case 'success':
        return 'Email Verified!'
      case 'error':
        return 'Verification Failed'
      default:
        return ''
    }
  }

  const getDescription = () => {
    switch (status) {
      case 'verifying':
        return 'Please wait while we verify your email address.'
      case 'success':
        return `${message}${email ? " You'll be redirected to complete your account setup." : ' You can now sign in to your account.'}`
      case 'error':
        return message
      default:
        return ''
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card border border-border shadow-clean rounded-3xl overflow-hidden w-full max-w-2xl p-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src={logo} alt="Gathio Logo" className="w-8 h-8" />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>
        </div>

        <div className="text-center mb-10">
          <div className={`w-16 h-16 ${getStatusColor()} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {getStatusIcon()}
          </div>

          <h2 className="text-4xl font-bold text-foreground mb-3 tracking-tight">
            {getTitle()}
          </h2>

          <p className="text-xl text-muted-foreground leading-relaxed">
            {getDescription()}
          </p>

          {email && status === 'success' && (
            <div className="bg-muted/50 border border-border rounded-xl p-4 mt-6">
              <p className="text-sm text-muted-foreground mb-1">Verified email:</p>
              <p className="font-medium text-foreground">{email}</p>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="space-x-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl transition-colors"
                >
                  Sign Up Again
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-6 py-3 border border-border text-foreground hover:bg-accent font-medium rounded-xl transition-colors"
                >
                  Sign In
                </Link>
              </div>
              <p className="text-sm text-muted-foreground">
                Need a new verification email?{' '}
                <Link to="/check-email" className="text-primary hover:underline">
                  Request resend
                </Link>
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="animate-pulse text-sm text-muted-foreground">
                Redirecting you to complete your setup...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
